// Cores base para preto e branco
const baseBlack = 'rgba(35, 43, 51, 1)';
const baseWhite = 'rgb(255, 255, 255)';

// Nosso espaçamento é baseado em múltiplos de 8
// usamos essa variável como base para isso
export const unit = 8;

// cores base para os textos
export const textColor = baseBlack;
export const textColorLight = baseWhite;

export interface LiteralObject {
  [prop: string]: any;
}

export interface ThemeProperty {
  [prop: string]: string | LiteralObject;
}

const transitions: ThemeProperty = {
  slow: '0.7s ease-in-out',
  fast: '0.3s ease-in-out',
  effect: {
    brightnessHigh: 'brightness(1.1)',
  },
};

const colors: ThemeProperty = {
  black: {
    base: baseBlack,
    '90': 'rgba(35, 43, 51, 0.9)',
    '80': 'rgba(35, 43, 51, 0.8)',
    '70': 'rgba(35, 43, 51, 0.7)',
    '50': 'rgba(35, 43, 51, 0.56)',
    '24': 'rgba(35, 43, 51, 0.24)',
    '15': 'rgba(35, 43, 51, 0.15)',
    '12': 'rgba(35, 43, 51, 0.12)',
    '10': 'rgba(35, 43, 51, 0.1)',
    '08': 'rgba(35, 43, 51, 0.08)',
    '04': 'rgba(35, 43, 51, 0.04)',
  },

  white: {
    base: baseWhite,
    '90': 'rgba(255, 255, 255, 0.9)',
    '50': 'rgba(255, 255, 255, 0.56)',
    '24': 'rgba(255, 255, 255, 0.24)',
    '16': 'rgba(255, 255, 255, 0.16)',
    '12': 'rgba(255, 255, 255, 0.12)',
    '08': 'rgba(255, 255, 255, 0.08)',
  },

  default: {
    base: '#dedede',
    light: '#e5e5e5',
    dark: '#d0d0d0',
    text: textColor,
  },

  primary: {
    base: '#4D46DF',
    light: '#6161ff',
    dark: '#3F32B7',
    text: textColorLight,
  },

  secondary: {
    base: '#E21571',
    text: textColorLight,
  },

  neutral: {
    base: '#62788E',
    light: '#E7EAEE;',
    text: textColorLight,
  },

  accent: {
    base: '#00d39c',
    light: '#00dba2',
    dark: '#00c693',
    text: textColorLight,
  },

  success: {
    base: '#00b865',
    light: '#6fcf97',
    dark: '#00c693',
    text: textColorLight,
  },

  warning: {
    base: '#f0af06',
    light: '#f6cf69',
    text: textColorLight,
  },

  danger: {
    base: '#ed6265',
    light: '#f29193',
    dark: '#a54446',
    text: textColorLight,
  },

  red: {
    base: '#dd2222',
  },
};

const spacing: ThemeProperty = {
  '1': `${unit}px`,
  '2': `${unit * 2}px`,
  '3': `${unit * 3}px`,
  '4': `${unit * 4}px`,
  '5': `${unit * 5}px`,
  '6': `${unit * 6}px`,
  '7': `${unit * 7}px`,
  '8': `${unit * 8}px`,
  '9': `${unit * 9}px`,
  '10': `${unit * 10}px`,
  '11': `${unit * 11}px`,
  '12': `${unit * 12}px`,
  '13': `${unit * 13}px`,
  '14': `${unit * 14}px`,
  small: `${unit / 2}px`,
  extraSmall: `${unit / 2 / 2}px`,
};

const borderRadius: ThemeProperty = {
  small: '2px',
  base: '6px',
  large: '12px',
};

const typography: ThemeProperty = {
  fontFamily: "'Outfit', sans-serif",
  fontSize: '16px',
  color: textColor,
  body1: {
    fontSize: '16px',
    fontWeight: '400',
    letterSpacing: '0',
    lineHeight: '24px',
  },
  bodyLarge: {
    fontSize: '18px',
    fontWeight: '400',
    letterSpacing: '0',
    lineHeight: '24px',
  },
  caption1: {
    fontSize: '14px',
    fontWeight: '500',
    letterSpacing: '0',
    lineHeight: '20px',
  },
  caption2: {
    fontSize: '12px',
    fontWeight: '400',
    letterSpacing: '0.2px',
    lineHeight: '16px',
  },
  caption2AllCaps: {
    fontSize: '12px',
    fontWeight: '400',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    lineHeight: '16px',
  },
  heading1: {
    fontSize: '75px',
    fontWeight: '500',
    letterSpacing: '-2px',
    lineHeight: '80px',
    marginTop: '0',
    marginBottom: '0',
  },
  heading2: {
    fontSize: '56px',
    fontWeight: '500',
    letterSpacing: '-1px',
    lineHeight: '70px',
    marginTop: '0',
    marginBottom: '0',
  },
  heading3: {
    fontSize: '46px',
    fontWeight: '500',
    letterSpacing: '-1px',
    lineHeight: '56px',
    marginTop: '0',
    marginBottom: '0',
  },
  heading4: {
    fontSize: '32px',
    fontWeight: '500',
    letterSpacing: '-0.6px',
    lineHeight: '36px',
    marginTop: '0',
    marginBottom: '0',
  },
  heading5: {
    fontSize: '24px',
    fontWeight: '500',
    lineHeight: '32px',
    letterSpacing: 'normal',
    marginTop: '0',
    marginBottom: '0',
  },
  heading6: {
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '30px',
    letterSpacing: '-0.2px',
    marginTop: '0',
    marginBottom: '0',
  },
  subtitle2: {
    fontSize: '18px',
    lineHeight: '22px',
    letterSpacing: '-0.35px',
  },
  button: {
    fontSize: '16px',
    letterSpacing: '-0.2px',
    textTransform: 'none',
    fontWeight: '500',
  },

  weights: {
    light: '300',
    regular: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },
};

export interface Theme {
  readonly colors: ThemeProperty;
  readonly spacing: ThemeProperty;
  readonly borderRadius: ThemeProperty;
  readonly typography: ThemeProperty;
  readonly transitions: ThemeProperty;
  /**
   * precisamos definir esse tipo para podermos acessar as propriedades
   * do tema programaticamente através de variáveis.
   * Exemplo:
   * const prop = 'colors';
   * props.theme[prop]
   * */
  readonly [prop: string]: any;
}

export const theme: Theme = {
  colors,
  spacing,
  borderRadius,
  typography,
  transitions,
};
